import React, {
  Dispatch,
  MutableRefObject,
  ReactNode,
  RefObject,
  SetStateAction,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import classnames from "classnames";
import cloneDeep from "lodash.clonedeep";
import {
  SortableContainer,
  SortableElement,
  SortEnd,
} from "react-sortable-hoc";
import { RGB, Font } from "@/sunzi";
import { getImageColor, i18n, urlToBlob, priceFormat } from "@/utils";
import { Drawer, Header, Icon, Loading, Wrapper } from "@/components";
import HeaderExpand from "@/components/header/expand";
import {
  Increment,
  RelatedProductIncrement,
  VipIncrement,
} from "@/increment/increment";
import { RelatedProduct, Vip, Double } from "@/increment";
import { ProductContext, SockTaskStatus } from ".";
import {
  CacheImageData,
  IConfirmOutput,
  IIncrementProduct,
  SockType,
  SockData,
  ProductType,
} from "./new-sock";
import sockConfig from "./new-sock-config";
import Sock3d from "./sock3d";
import Pattern from "./pattern";
import Animation from "./animation";
import Share from "./share";
import OptionPlugin from "./option-expand";
import Lettering from "./lettering";
import Apend from "./apend";
import { createAvatar, createWallpaper } from "./share-pattern";
import styles from "./style.less";

/** 袜子渲染默认背景色 */
const defaultBackgrandColor = { r: 228, g: 228, b: 228 };

/** 滚动组件容器 */
const SortableImageContainer = SortableContainer(
  ({ children }: { children: React.ReactNode }) => (
    <div className={styles.sortList}>{children}</div>
  )
);

/** 滚动组件 */
const SortableImage = SortableElement(
  ({
    source,
    showDelete,
    onDelete,
  }: {
    source: string;
    showDelete: boolean;
    onDelete: Dispatch<SetStateAction<void>>;
  }) => (
    <div className={styles.image}>
      <img src={source} />
      {showDelete && (
        <div className={styles.delete} onClick={() => onDelete()}>
          <Icon type="close-circle" color="#F56C6C" size={14} />
        </div>
      )}
    </div>
  )
);

interface CustomizeProps {
  /** 产品类型 */
  type: SockType;
  /** 袜子 3d 类 */
  sock3d: RefObject<Sock3d | undefined>;
  /** 贴图生成类 */
  pattern: RefObject<Pattern | undefined>;
  /** 动画类 */
  animation: RefObject<Animation | undefined>;
  /** 上一次任务状态 */
  prevTaskStatus: SockTaskStatus | undefined;
  /** 背景图缓存 */
  backgroundCache: MutableRefObject<CacheImageData>;
  /** 增值服务 */
  increment?: Increment;
  /** 第二次设计 */
  doubleDesign?: boolean;
  /** 当前用户刻字 */
  currentLettering: string;
  /** 分享链接 */
  shareLink?: string;
  /** 分享文案 */
  shareText?: string;
  /** 分享优惠价格 */
  sharePrice?: number;
  /** 分享优惠券图案 */
  shareCoupon?: string;
  /** 分享事件分类 */
  shareEventCategory?: string;
  /** 是否已分享 */
  isShared: boolean;
  /** 加车 loading */
  addCartLoading: boolean;
  /** 默认刻字文案 */
  defaultLineText?: string;
  /** 前缀文案 */
  prefixLineText?: string;
  /** 暴露 apend 函数 */
  apendRefFunction: any;
  /** 追加产品数据 */
  apendSockData?: SockData[];
  /** 设置当前用户刻字 */
  setCurrentLettering: Dispatch<SetStateAction<string>>;
  /** 设置当前要处理图片的下标 */
  setCurrentIndex: Dispatch<SetStateAction<number>>;
  /** zuploader 插件显影状态控制函数 */
  setZuploaderVisible: Dispatch<SetStateAction<boolean>>;
  /** 设置图片裁剪状态 */
  setCutState: Dispatch<SetStateAction<boolean>>;
  /** 设置是否已分享 */
  setIsShared: Dispatch<SetStateAction<boolean>>;
  /** 追加产品变体切换 */
  onApendVariantChange?: (data: ProductType) => void;
  /** 新的设计 */
  onNewDesign?: (output: IConfirmOutput) => void;
  /** 关闭回调 */
  onClose: () => void;
  /** 确认回调 */
  onConfirm: (
    output: IConfirmOutput,
    apendIds?: { sizeId: string; colorId: string }
  ) => void;
  /** 谷歌分析 */
  gtag: any;
}

const Customize: React.FC<CustomizeProps> = ({
  type,
  sock3d,
  pattern,
  animation,
  prevTaskStatus,
  backgroundCache,
  increment,
  doubleDesign,
  currentLettering,
  shareLink,
  shareText,
  sharePrice,
  shareCoupon,
  shareEventCategory,
  isShared,
  addCartLoading,
  defaultLineText,
  prefixLineText,
  apendSockData,
  apendRefFunction,
  setCurrentLettering,
  setZuploaderVisible,
  setCurrentIndex,
  setCutState,
  setIsShared,
  gtag,
  onClose,
  onConfirm,
  onNewDesign,
  onApendVariantChange,
}) => {
  /** 袜子数据 */
  const {
    shop,
    allPrice,
    avatarIncrease,
    lineTextsIncrease,
    productSelected,
    images,
    setImages,
    setTaskStatus,
    backgroundImage,
  } = useContext(ProductContext);

  /** 是否能添加头像 */
  const isAddImage = images.length < (pattern.current?.getImageCount() ?? 0);
  /** 已选择的尺寸 */
  const selectedSize: string =
    backgroundCache.current[productSelected.size ?? 0].size;
  /** 已选择的颜色 */
  const selectedColor: string =
    backgroundCache.current[productSelected.size ?? 0][
      productSelected.color ?? 0
    ].name;
  /** 字体颜色 */
  const fontColors: string[] =
    backgroundCache.current[productSelected.size ?? 0][
      productSelected.color ?? 0
    ]?.fontColors;
  /** 字体 */
  const fonts: Font[] | undefined =
    pattern.current?.options[productSelected.size ?? 0].textVersion
      ?.lineTexts![0].fonts;

  /** 底部图像 DOM */
  const totalContentDom = useRef<HTMLDivElement | null>(null);
  /** THREE 渲染 DOM */
  const _element = useRef<HTMLDivElement | null>(null);
  /** 编辑引导对应的 DOM */
  const _guideDependDom = useRef<HTMLDivElement | null>(null);
  /** 编辑引导 ReactNode */
  const _guideDom = useRef<ReactNode | null>(null);
  /** 限制字数 */
  let letteringLength = useRef<number>(0);
  /** 上次动画前的相机和控制器位置 */
  const originalPosition = useRef<any>({
    camera: undefined,
    controls: undefined,
  });
  /** headerExpand 组件 */
  const _expand = useRef<HeaderExpand>(null);
  /** 袜子生成的底图 Url */
  const _bottomImageURL = useRef<string>("");
  /** 分享头像 */
  const _avatarImage = useRef<string>("");
  /** 分享壁纸 */
  const _wallpaperImage = useRef<string>("");
  /** 渲染背景颜色 */
  const _backgroundColor = useRef<RGB>(defaultBackgrandColor);
  /** 是否追加产品 */
  const _apendIds = useRef<{ sizeId: string; colorId: string }>();
  /** 刻字显影 */
  const [letteringVisible, setLetteringVisible] = useState<boolean>(false);
  /** 刻字头部显影 */
  const [letteringHeaderVisible, setLetteringHeaderVisible] =
    useState<boolean>(false);
  /** 相关产品增值弹窗显影 */
  const [incrementVisible, setIncrementVisible] = useState<boolean>(false);
  /** 二份折扣弹框 */
  const [doubleIncrementVisible, setDoubleIncrementVisible] = useState(false);
  /** vip 弹窗显影 */
  const [vipIncrementVisible, setVipIncrementVisible] =
    useState<boolean>(false);
  /** 刻字 input 标签输入内容 */
  const [letteringText, setLetteringText] = useState<string>("");
  /** 当前选择 tab 的下标 */
  const [tabIndex, setTabIndex] = useState<number>(0);
  /** 底部图像是否到滚到右边 */
  const [isRightEnd, setIsRightEnd] = useState<boolean>(false);
  /** 头部展开状态改变 */
  const [expandChange, setExpandChange] = useState<boolean>(false);
  /** expand 头部显影 */
  const [expandHeaderVisible, setExpandHeaderVisible] = useState<boolean>(true);
  /** 编辑引导 */
  const [guideVisible, setGuideVisible] = useState<boolean>(false);
  /** 分享弹窗显影 */
  const [shareVisible, setShareVisible] = useState<boolean>(false);
  /** 加载模型 loading */
  const [loading, setLoading] = useState<boolean>(true);
  /** 当前选择的字体颜色 */
  const [fontColor, setFontColor] = useState<string | undefined>(
    fontColors && fontColors[0]
  );
  /** 默认选中 font */
  const currentFont: Font | undefined =
    pattern.current?.options[productSelected.size ?? 0].textVersion
      ?.lineTexts![0].font;
  /** 当前选择的字体 */
  const [font, setFont] = useState<Font | undefined>(currentFont);
  /** 背景图 */
  const [backgroundUrl, setBackgroundUrl] = useState<string | undefined>();
  /** 背景色 */
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>();
  /** vip 是否选中 */
  const [isVipSelected, setIsVipSelected] = useState<boolean>(false);
  /** 增值产品输出数据 */
  const incrementProduct = useRef<IIncrementProduct>({});
  /** 追加产品页面显影 */
  const [apendVisible, setApendVisible] = useState<boolean>(false);

  /** 显示字体选择 */
  const showFontSelect = font && fonts && fonts.length > 1;
  /** 显示字体颜色选择 */
  const showFontColorSelect = fontColors && fontColors.length > 1;
  /** 总图片个数 */
  const maxCount = pattern.current!.getImageCount();

  /** 总价格 */
  let totalPrice: number | string = allPrice.price;
  if (avatarIncrease) {
    if (pattern.current?.isLettering && !defaultLineText) {
      // 只有非默认文案的时候加上
      if (maxCount < images.length)
        totalPrice +=
          avatarIncrease?.price * maxCount + (lineTextsIncrease?.price ?? 0);
      else
        totalPrice +=
          avatarIncrease?.price * (images.length - 1) +
          (lineTextsIncrease?.price ?? 0);
    } else {
      totalPrice += avatarIncrease?.price * (images.length - 1);
    }
  }
  if (isShared && shareLink && sharePrice) {
    totalPrice -= sharePrice;
  }
  totalPrice = totalPrice.toFixed(2);

  /** 底部图像滚动事件 */
  const handleTotalScroll = (event: React.UIEvent<HTMLDivElement>) => {
    const { clientWidth, scrollWidth, scrollLeft } = event.currentTarget;
    const isRight = scrollLeft + clientWidth === scrollWidth;
    setIsRightEnd(isRight);
  };

  /** 排序结束 */
  const handleSortEnd = (sort: SortEnd) => {
    const { oldIndex, newIndex } = sort;
    let imagesTmp = cloneDeep(images);
    imagesTmp.splice(
      oldIndex,
      1,
      ...imagesTmp.splice(newIndex, 1, imagesTmp[oldIndex])
    );
    setImages(imagesTmp);
  };

  /** 详情头部确认事件 */
  const handleHeaderConfirm = () => {
    gtag("event", "event_tap_confirm");
    if (productSelected.size && pattern.current) {
      const option = pattern.current.options[productSelected.size];
      setExpandHeaderVisible(false);
      // 当前数据有刻字数据的时候提示用户刻字
      if (option.textVersion?.lineTexts) {
        // 设置刻字最大字数
        letteringLength.current =
          option.textVersion.lineTexts[0].maxLength ?? 0;
        setLetteringVisible(true);
        originalPosition.current = {
          camera: sock3d.current?.camera.position.clone(),
          controls: sock3d.current?.orbitControls.target.clone(),
        };
        if (pattern.current) {
          pattern.current.isLettering = true;
          renderMesh(
            i18n.format("modules.new.sock.your.text").toLocaleUpperCase()
          );
        }
        animation.current?.amplify();
      } else if (shareLink) {
        setShareVisible(true);
      } else {
        handleIncrementOpen();
      }
    }
  };

  /** 用户确认刻字 */
  const handleAdd = () => {
    gtag("event", "event_tap_add_text");
    setLetteringVisible(false);
    setLetteringHeaderVisible(true);
    animation.current?.confirm();
    if (pattern.current) {
      pattern.current.isLettering = true;
    }
  };

  /** 用户不刻字 */
  const handleWithout = (isClose: boolean = false) => {
    gtag("event", "event_tap_no_text");
    if (pattern.current && !defaultLineText) {
      pattern.current.isLettering = false;
      setCurrentLettering("");
    } else {
      setCurrentLettering(defaultLineText!);
    }
    setLetteringVisible(false);
    if (!isClose) {
      setExpandHeaderVisible(false);
      if (shareLink) {
        setShareVisible(true);
      } else {
        handleIncrementOpen();
      }
    } else {
      setExpandHeaderVisible(true);
    }

    renderMesh();
    const { camera, controls } = originalPosition.current;
    animation.current?.reSize(camera, controls);
  };

  /** 刻字事件 */
  const handleLetteringTextChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const text = event.target.value.toLocaleUpperCase();
    if (text.length <= letteringLength.current) {
      setLetteringText(text);
      setCurrentLettering(text);
    }
  };

  /** 刻字确认 */
  const handleLetteringConfirm = () => {
    const { camera, controls } = originalPosition.current;
    setLetteringHeaderVisible(false);
    setLetteringText("");
    if (shareLink) {
      setShareVisible(true);
    } else {
      handleIncrementOpen();
    }
    animation.current?.reSize(camera, controls);
  };

  /** 退出刻字 */
  const handleExitLettering = () => {
    const { camera, controls } = originalPosition.current;
    setLetteringHeaderVisible(false);
    setExpandHeaderVisible(true);
    setLetteringText("");
    if (!defaultLineText) {
      setCurrentLettering("");
    } else {
      setCurrentLettering(defaultLineText);
    }
    animation.current?.reSize(camera, controls);
    if (pattern.current && !defaultLineText)
      pattern.current.isLettering = false;
  };

  /** 分享弹窗关闭 */
  const handleShareClose = () => {
    setShareVisible(false);
    handleIncrementOpen();
  };

  /** 增值服务弹窗显示 */
  const handleIncrementOpen = () => {
    setExpandHeaderVisible(true);
    if (increment?.relatedProduct) {
      setIncrementVisible(true);
      return;
    }
    handleDoubleIncrementOpen();
  };

  /** 二份折扣增值服务弹框 */
  const handleDoubleIncrementOpen = () => {
    if (increment?.doubleDesign) {
      setDoubleIncrementVisible(true);
      return;
    }
    // handleVipIncrementOpen();
    handleApendOpen();
  };

  /** 追加产品弹框 */
  const handleApendOpen = () => {
    if (apendSockData) {
      setApendVisible(true);
      return;
    }
    handleVipIncrementOpen();
  };

  /** vip增值服务弹框 */
  const handleVipIncrementOpen = () => {
    if (increment?.vip) {
      setVipIncrementVisible(true);
      return;
    }
    handleVipIncrementConfirm();
  };

  /**
   * 增值服务确认回调
   * @param product 增值产品
   */
  const handleIncrementConfirm = (product?: RelatedProductIncrement) => {
    incrementProduct.current.relatedProduct = product;
    handleDoubleIncrementOpen();
  };

  /**
   * 二份增值确认回调
   * @param newDesign 新的设计
   * undefined 无二份
   * false 相同的设计
   * true 新的设计
   */
  const handleDoubleIncrementConfirm = (newDesign?: boolean) => {
    if (newDesign) {
      onNewDesign?.({ data: incrementProduct.current, fontColor, font });
      return;
    }
    incrementProduct.current.doubleDesign = newDesign;
    // handleVipIncrementOpen();
    handleApendOpen();
  };

  /** 追加产品确认回调 */
  const handleApendConfirm = (addApend?: {
    sizeId: string;
    colorId: string;
  }) => {
    _apendIds.current = addApend;
    setApendVisible(false);
    handleVipIncrementOpen();
  };

  /**
   * vip增值服务确认回调
   * @param product 增值VIP服务
   */
  const handleVipIncrementConfirm = (product?: VipIncrement) => {
    incrementProduct.current.vip = product;
    onConfirm(
      {
        data: incrementProduct.current,
        fontColor,
        font,
      },
      _apendIds.current
    );
  };

  /** 渲染模型 */
  const renderMesh = async (text?: string) => {
    if (_element.current && sock3d.current && pattern.current) {
      const patternImages = images.map((item) => ({
        image: item.image,
        faceRect: item.faceRect,
      }));
      if (prefixLineText || currentLettering || text) {
        let renderText = prefixLineText
          ? `${prefixLineText} ${currentLettering}`
          : currentLettering;
        if (text) {
          renderText = text;
        }
        _bottomImageURL.current = await pattern.current.toDataURL(
          patternImages,
          renderText,
          fontColor,
          font
        );
      } else {
        _bottomImageURL.current = await pattern.current.toDataURL(
          patternImages
        );
      }

      // 生成颜色
      const { size, color } = productSelected;
      let image =
        backgroundCache.current[size!][color!].variantImage ||
        backgroundCache.current[size!][color!].image;
      if (
        !backgroundCache.current[size!][color!].variantImage?.includes("http")
      ) {
        image = backgroundCache.current[size!][color!].image;
      }
      const colors: RGB[] = await getImageColor(image);
      createShareImage(colors[0] ?? defaultBackgrandColor);
      // 更新 3d 渲染区域背景图
      const canvas = sock3d.current!.renderer.domElement;
      canvas.style.zIndex = "1";
      canvas.style.position = "relative";
      if (backgroundImage) {
        if (!backgroundUrl) {
          const backgroundBlob = await urlToBlob(backgroundImage);
          const blobUrl = URL.createObjectURL(backgroundBlob);
          setBackgroundUrl(blobUrl);
        }
      } else {
        const bgColor = colors[0] || defaultBackgrandColor;
        setBackgroundColor(`rgb(${bgColor.r}, ${bgColor.g}, ${bgColor.b})`);
      }

      sock3d.current.render(_element.current);
      await sock3d.current.updateTexture(_bottomImageURL.current);
      setLoading(false);
      // 合并模型加载和裁剪的加载
      setCutState(true);
    }
  };

  /** 生成分享图片 */
  const createShareImage = async (backgroundColor: RGB) => {
    const [avatarImage, wallpaperImage] = await Promise.all([
      createAvatar(images[0].image, backgroundColor),
      createWallpaper(images[0].image, backgroundColor),
    ]);
    _backgroundColor.current = backgroundColor;
    _avatarImage.current = avatarImage;
    _wallpaperImage.current = wallpaperImage;
  };

  useEffect(() => {
    const isInitial =
      Math.round(sock3d.current!.camera.position.x) ===
      sockConfig[type].cameraPosition[0];
    const initialPosition = sockConfig[type].initialCamera;
    if (isInitial && prevTaskStatus === SockTaskStatus.Crop)
      animation.current!.initial(initialPosition, 2500);
  }, [loading]);

  useEffect(() => {
    // 更新已选中的底图和尺寸
    if (productSelected.color && productSelected.size && pattern.current) {
      pattern.current.bottomImageSelected = productSelected.color;
      pattern.current.sizeSelected = productSelected.size;
    }
    // 如果有头像的话才渲染
    if (images.length > 0) {
      renderMesh();
      if (images.length === 1) {
        setTabIndex(0);
      }
    }
    // 如果当前字体颜色在字体里找不到的话就默认选中第一个
    if (fontColors && fontColor) {
      const isFindColor = fontColors.indexOf(fontColor);
      if (isFindColor === -1) {
        setFontColor(fontColors[0]);
      }
    }
  }, [
    images,
    currentLettering,
    // pattern.current?.isLettering,
    productSelected.color,
    productSelected.size,
    fontColor,
    font,
  ]);

  useEffect(() => {
    // 设置引导 dom
    if (_guideDependDom.current) {
      const { offsetLeft, offsetWidth } = _guideDependDom.current;
      _guideDom.current = (
        <div
          className={styles.guideTips}
          style={{
            left: offsetLeft + offsetWidth / 2,
          }}
        >
          <span>{i18n.format("modules.new.sock.tab.and.edit")}</span>
        </div>
      );
    }
    // 如果没有滚动条就直接判断已经滚动到最右边
    if (totalContentDom.current) {
      const { scrollWidth, clientWidth } = totalContentDom.current;
      setIsRightEnd(scrollWidth === clientWidth);
    }
    // 如果用户第一次登陆显示编辑引导
    setGuideVisible(
      !!parseInt(
        window.localStorage.getItem("_sunzi_newsock_edit_guide") || "1"
      )
    );
    window.localStorage.setItem("_sunzi_newsock_edit_guide", "0");
    setTimeout(() => {
      setGuideVisible(false);
    }, 6000);
  }, []);

  // 底部标签选项
  const tabTitle: React.ReactNode[] = [
    // total tab布局
    <div className={styles.tabTotal}>
      <span className={styles.totalTitle}>
        {i18n.format("modules.new.sock.total")}:&nbsp;
      </span>
      <span className={styles.totalPrice}>
        {priceFormat(shop.currencySymbol, totalPrice)}
      </span>
      <svg className={styles.totalArc} viewBox="0 0 30 22">
        <path d="M0,0 C20,0 20,22 30,22 C33.4922712,22 23.0339379,22 0,22" />
        <rect x="0" y="0" width="30" height="22" />
      </svg>
    </div>,
    // sort tab布局
    <div className={styles.tabSort}>
      <svg className={styles.sortLeftArc} viewBox="0 0 30 22">
        <path d="M0,0 C20,0 20,22 30,22 C33.4922712,22 23.0339379,22 0,22" />
        <rect x="0" y="0" width="30" height="22" />
      </svg>
      <Icon className={styles.sortIcon} type="sort" size={16} />
      <span className={styles.sortTitle}>
        {i18n.format("modules.new.sock.sort")}
      </span>
      <svg className={styles.sortRightArc} viewBox="0 0 30 22">
        <path d="M0,0 C20,0 20,22 30,22 C33.4922712,22 23.0339379,22 0,22" />
      </svg>
    </div>,
  ];

  // 底部标签内容
  const tabContent: React.ReactNode[] = [
    // total 标签内容
    <div
      className={styles.totalContent}
      onScroll={handleTotalScroll}
      ref={totalContentDom}
    >
      {images.map((item, index) => {
        return index < maxCount ? (
          <div
            ref={(div) => index === 0 && (_guideDependDom.current = div)}
            key={index}
            className={classnames(styles.image, {
              [styles.guide]: guideVisible && index === 0,
            })}
            onClick={() => {
              setGuideVisible(false);
              setCurrentIndex(index);
              setTaskStatus(SockTaskStatus.Edit);
            }}
          >
            <img src={item.image} />
          </div>
        ) : null;
      })}
      {isAddImage && (
        <div
          className={classnames(styles.add, {
            [styles.addRightEnd]: isRightEnd,
          })}
        >
          <div
            className={styles.addButton}
            onClick={() => {
              setGuideVisible(false);
              setZuploaderVisible(true);
            }}
          >
            <div className={styles.addPrice}>{`+${priceFormat(
              shop.currencySymbol,
              avatarIncrease!.price
            )}`}</div>
            <Icon type="add" color="#FF533A" size={30} />
          </div>
        </div>
      )}
    </div>,
    // sort 标签内容
    <div className={styles.sortContent}>
      <SortableImageContainer
        axis="xy"
        onSortEnd={handleSortEnd}
        distance={1}
        helperClass={styles.sortableHelper}
      >
        {images.map((item, index) => {
          return index < maxCount ? (
            <div key={index} className={styles.sortImage}>
              <SortableImage
                source={item.image}
                index={index}
                showDelete={images.length !== 1}
                onDelete={() => {
                  let imagesTmp = cloneDeep(images);
                  imagesTmp.splice(index, 1);
                  setImages(imagesTmp);
                }}
              />
            </div>
          ) : null;
        })}
      </SortableImageContainer>
    </div>,
  ];

  return (
    <div className={styles.customize}>
      {expandHeaderVisible && (
        <Header.Expand
          ref={_expand}
          onClose={onClose}
          closeIconColor="#000"
          expandDisabled={doubleDesign}
          expandClose={() => setExpandChange(!expandChange)}
          closeIconType={"close"}
          renderContent={
            <div className={styles.headerSelected}>
              <div className={styles.selectedInfo}>
                <div className={styles.selectedLayout}>
                  <span className={styles.optionTitle}>{`${i18n.format(
                    "modules.new.sock.color"
                  )}: `}</span>
                  <span className={styles.optionSelected}>{selectedColor}</span>
                </div>
                <div className={styles.selectedLayout}>
                  <span className={styles.optionTitle}>{`${i18n.format(
                    "modules.new.sock.size"
                  )}: `}</span>
                  <span className={styles.optionSelected}>{selectedSize}</span>
                </div>
              </div>
            </div>
          }
          renderSubmit={
            <div className={styles.headerConfirm} onClick={handleHeaderConfirm}>
              {i18n.format("modules.global.confirm")}
            </div>
          }
        >
          <OptionPlugin
            expandChange={expandChange}
            backgroundCache={backgroundCache}
            onClose={() => _expand.current?.onExpandVisible()}
          />
        </Header.Expand>
      )}
      {letteringHeaderVisible && (
        <Header
          className={classnames(styles.headerLettering, {
            [styles.haveFontColor]: fontColors && fontColors.length > 1,
          })}
          onClose={handleExitLettering}
          closeIconColor="#000"
          closeIconType={"left"}
          renderContent={
            <div className={styles.headerLetteringContent}>
              <input
                className={styles.input}
                value={letteringText}
                onChange={handleLetteringTextChange}
                autoFocus
              />
              <div
                className={styles.inputCount}
              >{`${letteringText.length}/${letteringLength.current}`}</div>
            </div>
          }
          renderSubmit={
            <div
              className={classnames(styles.headerLetteringConfirm, {
                [styles.confirmDisable]: !letteringText,
              })}
              onClick={handleLetteringConfirm}
            >
              <Icon type="confirm" color="#fff" size={30} />
            </div>
          }
        />
      )}
      {letteringHeaderVisible && (showFontColorSelect || showFontSelect) && (
        <div className={styles.fontOptionsWrap}>
          {letteringHeaderVisible && showFontSelect && (
            <div className={styles.fontSelect}>
              <div className={styles.options}>
                {fonts!.map((item, index) => (
                  <div key={index} className={styles.fontWrap}>
                    <div
                      className={classnames(styles.font, {
                        [styles.fontSelected]: font!.id === item.id,
                      })}
                      onClick={() => setFont(item)}
                      style={{ fontFamily: item.name }}
                    >
                      {i18n.format("components.edit.font.family")}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {letteringHeaderVisible && showFontColorSelect && (
            <div className={styles.fontColorSelect}>
              <div className={styles.title}>
                {i18n.format("components.edit.font.color")}
              </div>
              <div className={styles.options}>
                {fontColors.map((item, index) => (
                  <div key={index} className={styles.optionWrap}>
                    <div
                      className={classnames(styles.fontColor, {
                        [styles.fontColorSelected]: fontColor === item,
                      })}
                      onClick={() => setFontColor(item)}
                      style={{ backgroundColor: item }}
                    >
                      <div className={styles.selectedIcon}>
                        <Icon type="tick-circle" color="#ff533a" size={15} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      <div ref={_element} className={styles.preview}>
        {loading && (
          <div className={styles.loadingWrap}>
            <Loading color="#ff533a" />
          </div>
        )}
        <div
          style={{
            backgroundImage: `url(${backgroundUrl})`,
            backgroundColor: backgroundColor,
          }}
          className={styles.background}
        />
      </div>
      {expandHeaderVisible && (
        <div className={styles.footer}>
          {guideVisible && _guideDom.current}
          <div className={styles.tabs}>
            <div className={styles.tabList}>
              {tabTitle.map((item, index) => (
                <div
                  key={index}
                  className={classnames(styles.tab, {
                    [styles.tabSelected]: tabIndex === index,
                    [styles.tabInvisible]: index === 1 && images.length <= 1,
                  })}
                  onClick={() => {
                    if (index === 1) gtag("event", "event_tap_sort");
                    setTabIndex(index);
                  }}
                >
                  {item}
                </div>
              ))}
            </div>
            <div className={styles.tabContent}>
              {tabContent.map((item, index) => (
                <div
                  key={index}
                  className={classnames(styles.tabPane, {
                    [styles.tabHide]: tabIndex !== index,
                  })}
                >
                  {item}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {shareVisible && (
        <Share
          shareLink={shareLink}
          shareText={shareText}
          sharePrice={sharePrice}
          shareCoupon={shareCoupon}
          shareEventCategory={shareEventCategory}
          avatarImage={_avatarImage.current}
          wallpaperImage={_wallpaperImage.current}
          sockColor={
            backgroundCache.current[productSelected.size as string][
              productSelected.color as string
            ].imageUrl
          }
          price={totalPrice}
          gtag={gtag}
          setIsShared={setIsShared}
          onClose={handleShareClose}
        />
      )}
      {letteringVisible && (
        <Lettering
          defaultLineText={defaultLineText}
          onClose={handleWithout}
          onAdd={handleAdd}
        />
      )}
      {incrementVisible && (
        <Wrapper className={styles.incrementWrapper}>
          <Drawer duration={0.5} mask>
            <RelatedProduct
              data={increment?.relatedProduct}
              currencySymbol={shop.currencySymbol}
              gtag={gtag}
              onClose={() => setIncrementVisible(false)}
              onConfirm={handleIncrementConfirm}
            />
          </Drawer>
        </Wrapper>
      )}
      {doubleIncrementVisible && increment?.doubleDesign && (
        <Double
          data={increment?.doubleDesign}
          onClose={() => setDoubleIncrementVisible(false)}
          onConfirm={handleDoubleIncrementConfirm}
        />
      )}
      {vipIncrementVisible && (
        <Vip
          mask
          productContent={
            <div className={styles.vipContent}>
              {/* <div className={styles.price}>
                <span className={styles.current}>
                  {priceFormat(shop.currencySymbol, (isVipSelected ? Number(increment!.vip!.price) + price : price).toFixed(2))}
                </span>
                <span className={styles.origin}>
                  {priceFormat(shop.currencySymbol, (isVipSelected ? Number(increment!.vip!.price) + originPrice : originPrice).toFixed(2))}
                </span>
              </div> */}
              <div className={styles.selected}>
                <span className={styles.optionTitle}>{`${i18n.format(
                  "modules.new.sock.color"
                )}: `}</span>
                <span className={styles.optionSelected}>{selectedColor}</span>
              </div>
              <div className={styles.selected}>
                <span className={styles.optionTitle}>{`${i18n.format(
                  "modules.new.sock.size"
                )}: `}</span>
                <span className={styles.optionSelected}>{selectedSize}</span>
              </div>
            </div>
          }
          vipIncrement={increment?.vip}
          currencySymbol={shop.currencySymbol}
          onVipClick={(isVip: boolean) => setIsVipSelected(isVip)}
          onClose={() => setVipIncrementVisible(false)}
          onConfirm={handleVipIncrementConfirm}
          gtag={gtag}
        />
      )}
      {apendVisible && apendSockData && onApendVariantChange && (
        <Apend
          // ref={ref => ref && setApendRef(ref)}
          shop={shop}
          apendRefFunction={apendRefFunction}
          apendSockData={apendSockData!}
          onVariantChange={onApendVariantChange}
          onClose={() => setApendVisible(false)}
          onConfirm={handleApendConfirm}
        />
      )}
      {addCartLoading && (
        <Wrapper className={styles.addLoadingWrapper}>
          <Loading.Line />
        </Wrapper>
      )}
    </div>
  );
};

export default Customize;
